// This app's pages as code routes (platform plan 56, D139). Each takes its page from `PAGES`, the
// registry `pnpm gen` writes (platform D147): the path is its manifest's in `definitions/pages/`.

import { createRoute, useSearch } from "@tanstack/react-router";
import { ADMIN_TAB_IDS, PAGES, viewFromAddress } from "@app/shared";
import type { RuleKind } from "@app/shared";
import { AdminPage } from "./features/routing/admin-page";
import type { AdminTab } from "./features/routing/admin-page";
import { PreRoutePage } from "./features/routing/pre-route-page";
import { RoutingPage } from "./features/routing/routing-page";
import { ShippingPage } from "./features/routing/shipping-page";
import { SystemLogPage } from "./features/routing/system-log-page";
import { Route as rootRoute } from "./routes/__root";

/**
 * What a link may open a page on (the System log's Go to line): a line on the Worklist or History,
 * a Shipping tab with a line's or a pickup's rows selected, a shipment, and a Pre-route tab (the
 * alert badge), and an Admin tab (`admin`) with Routing rules' In use filter (`rules`: the Worklist's
 * "No routing rule" link). Anything else is dropped.
 */
/** Beside these, the Worklist's and History's filters as `v.*` keys (`view-url.ts`). */
export interface PageSearch {
  line?: string;
  tab?: "schedule" | "log" | "waiting" | "alerts" | "moved" | "all";
  shipment?: string;
  pickup?: string;
  admin?: AdminTab;
  rules?: RuleKind;
}
// Every tab, Import too: whether it is offered is the server's switch, read on the page (`adminTabOf`).
const ADMIN_TABS: readonly string[] = ADMIN_TAB_IDS;
const RULE_KINDS: readonly string[] = ["none", "app", "a2000"];
const TABS: readonly string[] = ["schedule", "log", "waiting", "alerts", "moved", "all"];
const id = (v: unknown) => (typeof v === "string" && /^[A-Za-z0-9:|_-]{1,80}$/.test(v) ? v : undefined);
const pageSearch = (s: Record<string, unknown>): PageSearch => ({
  ...(id(s.line) ? { line: id(s.line) } : {}),
  ...(typeof s.tab === "string" && TABS.includes(s.tab) ? { tab: s.tab as PageSearch["tab"] } : {}),
  ...(id(s.shipment) ? { shipment: id(s.shipment) } : {}),
  ...(id(s.pickup) ? { pickup: id(s.pickup) } : {}),
  ...(typeof s.admin === "string" && ADMIN_TABS.includes(s.admin) ? { admin: s.admin as AdminTab } : {}),
  ...(typeof s.rules === "string" && RULE_KINDS.includes(s.rules) ? { rules: s.rules as RuleKind } : {}),
  // The view's keys as they are: checked part by part when the page reads them (`viewOfParams`).
  ...Object.fromEntries(
    Object.entries(s).filter(
      ([k, v]) =>
        k.startsWith("v.") && (typeof v === "string" || (Array.isArray(v) && v.every((x) => typeof x === "string"))),
    ),
  ),
});

const route = (path: string, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => rootRoute, path, component, validateSearch: pageSearch });

const useLink = () => useSearch({ strict: false });

function Worklist() {
  const search = useLink() as PageSearch & Record<string, unknown>;
  return <RoutingPage key={search.line ?? ""} mode="worklist" line={search.line} view={viewFromAddress(search)} />;
}
function History() {
  const search = useLink() as PageSearch & Record<string, unknown>;
  return <RoutingPage key={search.line ?? ""} mode="history" line={search.line} view={viewFromAddress(search)} />;
}
function Shipping() {
  const { tab, shipment, line, pickup } = useLink();
  const shippingTab = tab === "schedule" || tab === "log" ? tab : undefined;
  return (
    <ShippingPage
      key={`${tab}|${shipment}|${line}|${pickup}`}
      tab={shippingTab}
      shipment={shipment}
      focus={{ line, pickup }}
    />
  );
}
function SystemLog() {
  const { line } = useLink() as PageSearch;
  // A line's details link here (Open in System log): that line's changes.
  return <SystemLogPage key={line ?? ""} line={line} />;
}
function Admin() {
  const { admin, rules } = useLink() as PageSearch;
  return <AdminPage tab={admin} rules={rules} />;
}
function PreRoute() {
  const { tab } = useLink();
  return <PreRoutePage key={tab ?? ""} tab={tab} />;
}

export const PAGE_ROUTES = [
  // ---- Production & Routing: custom pages over the routing entities (platform docs/reference/custom-pages.md).
  route(PAGES.worklist.path, Worklist),
  route(PAGES["pre-route"].path, PreRoute),
  route(PAGES.shipping.path, Shipping),
  route(PAGES.history.path, History),
  route(PAGES["system-log"].path, SystemLog),
  // Managers only (the Admin feature): the platform shows its tab to them alone, the server refuses the rest.
  route(PAGES.admin.path, Admin),
];
